import { useDispatch } from 'react-redux'
import { Button, ButtonProps, Dialog, DialogContent, DialogTitle } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { hideLevel, showLevel } from '../redux/slices/levels'
import { useTranslation } from 'react-i18next'
import { Level, Project } from '../types'
import { useState } from 'react'
import LevelForm from './LevelForm'
import { usePolicy } from '../contexts/PolicyContext'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface LevelCreateButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  project?: Project
  afterSubmit?: (level: Level) => void
}

export default function LevelCreateButton({
  project,
  afterSubmit,
  ...props
}: LevelCreateButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showLevel(undefined))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideLevel())
  }

  /**
   * Handle the after submit action.
   */
  function handleAfterSubmit(level: Level): void {
    afterSubmit?.(level)
    closeDialog()
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Button
          variant="contained"
          color="primary"
          endIcon={<AddIcon fontSize="inherit" />}
          {...props}
          onClick={() => openDialog()}
        >
          {t('newLevel')}
        </Button>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('newLevel')}</DialogTitle>
        <DialogContent>
          <LevelForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={handleAfterSubmit}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
